import { useEffect, useMemo, useRef, useState } from "react";
import { Engraved, Icon, Script, TopBar } from "../components/ui";
import type { ScreenId } from "../components/ui";
import { FloralSpray } from "../components/Florals";
import type { FloralDensity } from "../components/Florals";
import { PhotoTile } from "../components/PhotoTile";
import { useEvent } from "../lib/event";
import { api, putToR2 } from "../lib/api";

type UploadStep = "pick" | "uploading" | "done";

const EXT_TYPES: Record<string, string> = {
  mov: "video/quicktime",
  mp4: "video/mp4",
  m4v: "video/mp4",
  webm: "video/webm",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  png: "image/png",
  heic: "image/heic",
  heif: "image/heif",
  webp: "image/webp",
};

// Prefer the browser-reported MIME type; fall back to the file extension when it's
// missing (some platforms report an empty type for videos).
function fileContentType(f: File): string {
  if (f.type) return f.type;
  const ext = f.name.split(".").pop()?.toLowerCase() ?? "";
  return EXT_TYPES[ext] ?? "application/octet-stream";
}

export function UploadScreen({ go, density }: { go: (s: ScreenId) => void; density: FloralDensity }) {
  const { eventId, live } = useEvent();
  const [step, setStep] = useState<UploadStep>("pick");
  const [prog, setProg] = useState(0);
  const [doneCount, setDoneCount] = useState(0);

  // ---- mock camera-roll state (used when not live) ----
  const [sel, setSel] = useState<Record<number, boolean>>({});
  const lib = useMemo(() => Array.from({ length: 18 }, (_, i) => 300 + i), []);
  const selCount = Object.values(sel).filter(Boolean).length;
  const toggle = (s: number) => setSel((p) => ({ ...p, [s]: !p[s] }));

  // ---- live state ----
  const [files, setFiles] = useState<File[]>([]);
  const fileInput = useRef<HTMLInputElement>(null);

  // Simulated progress for the mock flow.
  useEffect(() => {
    if (step !== "uploading" || live) return;
    setProg(0);
    let p = 0;
    const id = setInterval(() => {
      p += Math.random() * 14 + 6;
      if (p >= 100) {
        p = 100;
        clearInterval(id);
        setDoneCount(selCount);
        setTimeout(() => setStep("done"), 420);
      }
      setProg(Math.min(100, p));
    }, 230);
    return () => clearInterval(id);
  }, [step, live, selCount]);

  // Real upload: process in small chunks (init -> parallel PUTs -> confirm) so any number
  // of files works within the Worker's per-request limits.
  async function runLiveUpload(picked: File[]) {
    if (!eventId || picked.length === 0) return;
    setStep("uploading");
    setProg(0);
    // Small chunks keep each request under the free Workers plan's 50-subrequest cap,
    // even for all-video batches (videos finalize inline, ~7 subrequests each).
    const CHUNK = 6;
    let completed = 0; // attempted (success or fail) — drives the progress ring
    let succeeded = 0;
    try {
      for (let start = 0; start < picked.length; start += CHUNK) {
        const batch = picked.slice(start, start + CHUNK);
        const { uploads } = await api.uploadInit({
          eventId,
          files: batch.map((f) => ({ filename: f.name, contentType: fileContentType(f), size: f.size })),
        });
        // Upload the batch in parallel; putToR2 already retries transient failures.
        const ids = await Promise.all(
          uploads.map(async (target, i) => {
            try {
              await putToR2(target.uploadUrl, target.headers, batch[i]!);
              succeeded++;
              return target.photoId;
            } catch (e) {
              console.error("put failed:", e);
              return null;
            } finally {
              completed++;
              setProg(Math.round((completed / picked.length) * 100));
            }
          }),
        );
        const okIds = ids.filter((id): id is string => id !== null);
        if (okIds.length) await api.uploadConfirm({ eventId, photoIds: okIds });
      }
      if (succeeded === 0) throw new Error("no files uploaded");
      setDoneCount(succeeded);
      setTimeout(() => setStep("done"), 300);
    } catch (err) {
      console.error("upload failed:", err);
      alert(`Upload failed: ${(err as Error).message}. Please try again.`);
      setStep("pick");
    }
  }

  if (step === "uploading") {
    return (
      <div className="screen upload-busy">
        <FloralSpray variant="sprig" density={density} width={200} height={150} seed={4} className="busy-sprig" />
        <div className="ring-wrap">
          <svg viewBox="0 0 120 120" className="prog-ring">
            <circle cx="60" cy="60" r="52" className="ring-bg" />
            <circle
              cx="60"
              cy="60"
              r="52"
              className="ring-fg"
              style={{ strokeDasharray: 2 * Math.PI * 52, strokeDashoffset: 2 * Math.PI * 52 * (1 - prog / 100) }}
            />
          </svg>
          <div className="ring-pct">
            {Math.round(prog)}
            <span>%</span>
          </div>
        </div>
        <Script size={34} style={{ color: "var(--c-accent)" }}>
          Adding your photos
        </Script>
        <Engraved size={11.5} style={{ color: "var(--c-ink-soft)" }}>
          {live ? files.length : selCount} items · keep this open
        </Engraved>
      </div>
    );
  }

  if (step === "done") {
    return (
      <div className="screen upload-done">
        <FloralSpray variant="cornerTL" density={density} width={170} height={170} seed={9} className="done-fl tl" />
        <FloralSpray variant="cornerTR" density={density} width={170} height={170} seed={21} className="done-fl tr" />
        <div className="done-check">
          <Icon name="check" size={40} stroke={2} />
        </div>
        <Script size={46} style={{ color: "var(--c-accent)" }}>
          Thank you
        </Script>
        <p className="done-line">
          <strong>{doneCount} memories</strong> added to Taner &amp; Abbie's album.
        </p>
        <Engraved size={10.5} style={{ color: "var(--c-ink-soft)", letterSpacing: "0.26em" }}>
          They'll appear in the moments shortly
        </Engraved>
        <div className="done-btns">
          <button className="btn primary" onClick={() => go("moments")}>
            Explore the album
          </button>
          <button
            className="btn ghost"
            onClick={() => {
              setSel({});
              setFiles([]);
              setStep("pick");
            }}
          >
            Add more
          </button>
        </div>
      </div>
    );
  }

  // ---- pick step ----
  if (live) {
    return (
      <div className="screen upload">
        <TopBar title="Add To The Album" onBack={() => go("home")} />
        <div className="upload-head">
          <Script size={36} className="up-title">
            Share your day
          </Script>
          <p className="up-sub">
            Pick the photos and videos you took. They go straight into the shared album — no sign-in, nothing to
            download.
          </p>
        </div>
        <input
          ref={fileInput}
          type="file"
          accept="image/*,video/*"
          multiple
          style={{ display: "none" }}
          onChange={(e) => setFiles(Array.from(e.target.files ?? []))}
        />
        <div style={{ padding: "8px 24px", textAlign: "center" }}>
          <button className="btn ghost wide" onClick={() => fileInput.current?.click()}>
            <Icon name="camera" size={18} style={{ marginRight: 8 }} />
            {files.length ? `${files.length} selected — choose again` : "Choose photos & videos"}
          </button>
        </div>
        <div className="upload-bar">
          <button className="btn primary wide" disabled={!files.length} onClick={() => runLiveUpload(files)}>
            {files.length ? `Upload ${files.length} item${files.length > 1 ? "s" : ""}` : "Select photos to upload"}
          </button>
        </div>
      </div>
    );
  }

  // mock camera-roll picker
  return (
    <div className="screen upload">
      <TopBar title="Add To The Album" onBack={() => go("home")} />
      <div className="upload-head">
        <Script size={36} className="up-title">
          Share your day
        </Script>
        <p className="up-sub">
          Pick the photos and videos you took. They go straight into the shared album — no sign-in, nothing to download.
        </p>
      </div>
      <div className="lib-meta">
        <Engraved size={11}>From your camera roll</Engraved>
        <button
          className="link-mini"
          onClick={() => {
            const all: Record<number, boolean> = {};
            lib.forEach((s) => (all[s] = true));
            setSel(all);
          }}
        >
          Select all
        </button>
      </div>
      <div className="lib-grid">
        {lib.map((s) => (
          <button key={s} className={"lib-cell" + (sel[s] ? " on" : "")} onClick={() => toggle(s)}>
            <PhotoTile seed={s} ratio="1 / 1" radius={5} />
            <span className="lib-tick">
              <Icon name="check" size={13} stroke={2.4} />
            </span>
          </button>
        ))}
      </div>
      <div className="upload-bar">
        <button className="btn primary wide" disabled={!selCount} onClick={() => selCount && setStep("uploading")}>
          {selCount ? `Upload ${selCount} item${selCount > 1 ? "s" : ""}` : "Select photos to upload"}
        </button>
      </div>
    </div>
  );
}
